import axios from 'axios';
import { API_BASE } from './apiConfig';
import { spaceClient, spaceClientFor } from './client';

// ── Gateway configuration ────────────────────────────────────────────────────

export const getPaymentGateways = () =>
  spaceClient.get('/payments/gateways', { withCredentials: true }).then((r) => r.data);

export const savePaymentGateway = (gateway, data) =>
  spaceClient.put(`/payments/gateways/${gateway}`, data, { withCredentials: true }).then((r) => r.data);

export const togglePaymentGateway = (gateway, isEnabled) =>
  spaceClient.patch(`/payments/gateways/${gateway}/toggle`, { isEnabled }, { withCredentials: true }).then((r) => r.data);

// ── Transactions ─────────────────────────────────────────────────────────────

// Filter params like status/gateway/transactionType can be arrays (multi-select) —
// serialise them as repeated params: ?status=Succeeded&status=Pending
const repeatArrayParamsSerializer = (params) => {
  const s = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    Array.isArray(value) ? value.forEach((item) => s.append(key, item)) : s.append(key, value);
  });
  return s.toString();
};

// Space-scoped: used by the finance role → GET /api/space/{spaceId}/payments/transactions
export const getTransactions = (params = {}) =>
  spaceClient.get('/payments/transactions', {
    params,
    paramsSerializer: repeatArrayParamsSerializer,
    withCredentials: true,
  }).then((r) => r.data);

// Space-scoped: finance role groups a batch of charges under one deposit_id
// after reconciling them against a real bank deposit they received.
export const assignDepositId = (chargeIds, depositId) =>
  spaceClient.put('/payments/transactions/deposit-id', { chargeIds, depositId }, { withCredentials: true }).then((r) => r.data);

// Entities with nested spaces — used by gms_accountant to populate space filter
export const getAdminEntities = () =>
  axios.get(`${API_BASE}/api/admin/entities`, { withCredentials: true }).then((r) => r.data);

// Global: used by gms_accountant → GET /api/transactions
// entityIds: filter to all spaces belonging to those entities (ignored when spaceIds provided)
// spaceIds:  filter to specific spaces
// All array filters (entityIds, spaceIds, status, gateway, transactionType) are
// serialised as repeated params: ?spaceIds=1&spaceIds=2
export const getTransactionsGlobal = ({ entityIds, spaceIds, ...rest } = {}) =>
  axios.get(`${API_BASE}/api/transactions`, {
    params: {
      ...(entityIds?.length ? { entityIds } : {}),
      ...(spaceIds?.length  ? { spaceIds  } : {}),
      ...rest,
    },
    paramsSerializer: repeatArrayParamsSerializer,
    withCredentials: true,
  }).then((r) => r.data);

// ── Deposits ─────────────────────────────────────────────────────────────────
// Finance groups charges under a free-text Deposit ID (see assignDepositId above)
// after reconciling them against a real bank deposit, then attaches that bank's
// wire-transfer confirmation document. This section is the Deposits tab's API
// surface: browsing deposits, drilling into one, and the "Add Deposit" wizard.

// Space-scoped: list deposits for the active space (Finance) —
// GET /api/space/{spaceId}/payments/deposits
export const getDeposits = (params = {}) =>
  spaceClient.get('/payments/deposits', { params, withCredentials: true }).then((r) => r.data);

// Global: cross-space deposits list, used by gms_accountant — GET /api/deposits
// entityIds/spaceIds: optional arrays, serialised as repeated params (mirrors getTransactionsGlobal)
export const getDepositsGlobal = ({ entityIds, spaceIds, ...rest } = {}) =>
  axios.get(`${API_BASE}/api/deposits`, {
    params: {
      ...(entityIds?.length ? { entityIds } : {}),
      ...(spaceIds?.length  ? { spaceIds  } : {}),
      ...rest,
    },
    paramsSerializer: repeatArrayParamsSerializer,
    withCredentials: true,
  }).then((r) => r.data);

// Space-scoped detail drill-down (own space, Finance) —
// GET /api/space/{spaceId}/payments/deposits/{depositId}
export const getDepositDetail = (depositId, params = {}) =>
  spaceClient.get(`/payments/deposits/${encodeURIComponent(depositId)}`, { params, withCredentials: true }).then((r) => r.data);

// Global detail drill-down (gms_accountant/global_admin) — spaceId is required here
// since a Deposit ID alone isn't globally unique, only unique within one space.
// GET /api/deposits/{depositId}?spaceId=
export const getDepositDetailGlobal = (spaceId, depositId, params = {}) =>
  axios.get(`${API_BASE}/api/deposits/${encodeURIComponent(depositId)}`, {
    params: { ...params, spaceId },
    withCredentials: true,
  }).then((r) => r.data);

// Space-scoped: unassigned charges picker for the "Add Deposit" wizard's step 1 —
// Finance-only (enforced server-side too). GET /api/space/{spaceId}/payments/charges/unassigned
export const getUnassignedCharges = (params = {}) =>
  spaceClient.get('/payments/charges/unassigned', { params, withCredentials: true }).then((r) => r.data);

// Space-scoped: upload/replace the wire transfer document for a deposit — Finance-only.
// FormData body; no manual Content-Type header, axios sets the multipart boundary itself.
// POST /api/space/{spaceId}/payments/transactions/deposit-id/{depositId}/wire-transfer-document
export const uploadWireDocument = (depositId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return spaceClient.post(
    `/payments/transactions/deposit-id/${encodeURIComponent(depositId)}/wire-transfer-document`,
    formData,
    { withCredentials: true },
  ).then((r) => r.data);
};

// Space-scoped: view/download the wire transfer document for the active space's deposit
// (Finance in their own space). GET on the same upload route, streamed back as a blob.
export const downloadWireDocument = (depositId) =>
  spaceClient.get(
    `/payments/transactions/deposit-id/${encodeURIComponent(depositId)}/wire-transfer-document`,
    { responseType: 'blob', withCredentials: true },
  ).then((r) => r.data);

// Explicit-space variant for gms_accountant/global_admin — there's no "active space" for
// that role, so the row's own spaceId is passed explicitly (see useSpaceId's null-for-
// gms_accountant behaviour). Same route/shape as downloadWireDocument, different space.
export const downloadWireDocumentFor = (spaceId, depositId) =>
  spaceClientFor(spaceId).get(
    `/payments/transactions/deposit-id/${encodeURIComponent(depositId)}/wire-transfer-document`,
    { responseType: 'blob', withCredentials: true },
  ).then((r) => r.data);
